/**
 * 縦書きプレビュー用の行分割ユーティリティ
 *
 * 1行あたりの文字数（wordCountOnVertical）で段落を行分割する。
 * 行頭禁則文字（句読点・閉じ括弧等）が行頭に来る場合は「追い出し方式」で
 * 直前の文字と一緒に次行へ送る（= 改行位置を手前にずらす）。
 *
 * プレビュー（ManuscriptPreview.vue）とページ数計算（ManuscriptPreviewPage.vue）の
 * 両方から利用し、行数の整合を保つ。
 * 仕様の詳細は docs/vert_preview.md を参照。
 */

/** 行頭に来てはいけない文字（句読点・閉じ括弧・小書き文字・長音・繰り返し記号など） */
export const LINE_HEAD_PROHIBITED_CHARS =
  "、。，．・：；？！ーヽヾゝゞ々〃〜～" +
  "ぁぃぅぇぉっゃゅょゎゕゖァィゥェォッャュョヮヵヶ" +
  "」』）｝〕》〉】］〙〗〟’”" +
  "‥…" +
  ",.:;?!)]}｣､･ｰﾞﾟ";

/** 行末に残してはいけない文字（開き括弧類） */
export const LINE_END_PROHIBITED_CHARS = "「『（｛〔《〈【［〘〖〝‘“([{｢";

/** 同じ文字の連続を分離してはいけない文字（三点リーダ・ダッシュ類） */
export const INSEPARABLE_CHARS = "…‥—―";

/** ルビ記法 |親文字《ルビ》 にマッチする正規表現を生成する */
// eslint-disable-next-line no-control-regex
const createRubyRegExp = () => new RegExp("[|｜][^|｜\r\n]*?《.*?》", "gu");

/**
 * ルビ記法 |親文字《ルビ》 を <ruby>親文字<rt>ルビ</rt></ruby> に変換する
 * @param paragraph 1段落分のテキスト（改行を含まない）
 */
export function convertRubyToHtml(paragraph: string): string {
  const textWithRuby = paragraph.match(createRubyRegExp());
  if (!textWithRuby) return paragraph;

  let result = paragraph;
  textWithRuby.forEach((text) => {
    let replaceText = text;
    replaceText = replaceText.replace(/[|｜]/, "<ruby>");
    replaceText = replaceText.replace("《", "<rt>");
    replaceText = replaceText.replace("》", "</rt></ruby>");
    result = result.replace(text, replaceText);
  });
  return result;
}

/**
 * 行分割の最小単位。
 * 通常の文字は1文字=1ユニット、<ruby>〜</ruby> は分断しないよう1ユニットにまとめる。
 */
interface Unit {
  /** 元のHTML断片（タグを含む） */
  html: string;
  /** 字数カウント（タグ・<rt>内の文字は数えない） */
  count: number;
  /** カウント対象の文字列（禁則判定用。コードポイント単位の配列） */
  chars: string[];
}

/** タグを考慮して段落をユニット列に分解する */
function toUnits(paragraph: string): Unit[] {
  const units: Unit[] = [];
  let ruby: Unit | null = null;
  let insideRt = false;

  const tokens = paragraph.match(/<[^>]+>|./gu) || [];
  tokens.forEach((token) => {
    if (token.startsWith("<")) {
      if (token.startsWith("<rt")) {
        insideRt = true;
      } else if (token.startsWith("</rt")) {
        insideRt = false;
      }

      if (token.startsWith("<ruby")) {
        ruby = { html: token, count: 0, chars: [] };
        return;
      }
      if (ruby) {
        ruby.html += token;
        if (token.startsWith("</ruby")) {
          units.push(ruby);
          ruby = null;
        }
        return;
      }
      units.push({ html: token, count: 0, chars: [] });
      return;
    }

    const countable = !insideRt;
    if (ruby) {
      ruby.html += token;
      if (countable) {
        ruby.count += 1;
        ruby.chars.push(token);
      }
      return;
    }
    units.push({ html: token, count: countable ? 1 : 0, chars: countable ? [token] : [] });
  });

  // 閉じタグが欠落している場合の保険
  if (ruby) units.push(ruby);

  return units;
}

/** i 番目以降で最初に行頭へ来るカウント対象文字を返す（先読み用） */
function nextHeadChar(units: Unit[], i: number): string {
  for (let j = i; j < units.length; j += 1) {
    if (units[j].count > 0) return units[j].chars[0];
  }
  return "";
}

/** 行末のカウント対象文字を返す */
function lineTailChar(line: Unit[]): string {
  for (let j = line.length - 1; j >= 0; j -= 1) {
    if (line[j].count > 0) return line[j].chars[line[j].chars.length - 1];
  }
  return "";
}

/**
 * 1段落を wordCountOnVertical 文字ごとの行に分割する（追い出し禁則つき）
 *
 * - 行頭禁則文字が次行頭に来る場合、行末の文字を一緒に次行へ追い出す
 * - 行末が開き括弧の場合、括弧を次行へ送る
 * - 分離禁止文字（……／――）の連続は行間で分断しない
 * - 追い出しは1行あたり最大 wordCountOnVertical / 2 文字まで（異常系のフォールバック）
 * - <ruby>〜</ruby> は分断せず、行をまたぐ場合はルビごと次行へ送る
 *
 * @param paragraph ルビ変換済みの1段落分のテキスト（改行を含まない）
 * @param wordCountOnVertical 1行あたりの文字数
 * @returns 行文字列の配列。プレビューは join("<br>")、ページ数計算は length を使う
 */
export function breakVerticalLines(paragraph: string, wordCountOnVertical: number): string[] {
  const units = toUnits(paragraph);
  const maxPushOutPerLine = Math.floor(wordCountOnVertical / 2);
  const lines: string[] = [];

  let i = 0;
  while (i < units.length) {
    const line: Unit[] = [];
    let count = 0;

    // 行を埋める（1ユニットが1行に収まらない長大ルビ等は単独行として許容する）
    while (i < units.length && (count + units[i].count <= wordCountOnVertical || count === 0)) {
      line.push(units[i]);
      count += units[i].count;
      i += 1;
    }

    // 追い出し禁則（続きの行がある場合のみ）
    if (i < units.length) {
      let pushedOut = 0;
      while (pushedOut < maxPushOutPerLine) {
        const headChar = nextHeadChar(units, i);
        const tailChar = lineTailChar(line);
        if (!headChar || !tailChar) break;

        const needPushOut =
          LINE_HEAD_PROHIBITED_CHARS.includes(headChar) ||
          LINE_END_PROHIBITED_CHARS.includes(tailChar) ||
          (INSEPARABLE_CHARS.includes(tailChar) && headChar === tailChar);
        if (!needPushOut) break;

        // 行末のカウント対象ユニットを特定し、後続のタグごと次行へ送り戻す
        let tailIndex = line.length - 1;
        while (line[tailIndex].count === 0) tailIndex -= 1;
        const moveCount = line[tailIndex].count;
        if (count - moveCount <= 0) break; // 行が空になる追い出しはしない

        while (line.length > tailIndex) {
          line.pop();
          i -= 1;
        }
        count -= moveCount;
        pushedOut += moveCount;
      }
    }

    lines.push(line.map((unit) => unit.html).join(""));
  }

  return lines;
}
